import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MessageCircle, Phone, Check, Clock, Shield, Award, Smartphone, MapPin, Users, ThumbsUp, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import BookingModal from '@/components/BookingModal';
import JsonLd, { generateProductSchema, generateBreadcrumbSchema } from '@/components/seo/JsonLd';
import FAQSection from '@/components/FAQSection';
import SEOHead from '@/components/seo/SEOHead';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import OptimizedImage from '@/components/ui/OptimizedImage';
import iphoneImage from '@/assets/iphone-repair.jpg';

const screenReplacementSchema = generateProductSchema({
  name: 'iPhone Screen Replacement Service in Patna, Bihar',
  description: 'Professional iPhone screen replacement in Patna. Same-day service for cracked, broken, or unresponsive iPhone screens. OLED & LCD replacement for all models from iPhone 6 to iPhone 16 Pro Max with 3-month warranty.',
  url: 'https://ifixappleservicecenter.in/iphone-screen-replacement',
  priceRange: '₹2,499 - ₹29,999',
  ratingValue: 4.9,
  reviewCount: 420,
});

const screenBreadcrumbSchema = generateBreadcrumbSchema([
  { name: 'Home', url: 'https://ifixappleservicecenter.in/' },
  { name: 'iPhone Service', url: 'https://ifixappleservicecenter.in/iphone-service' },
  { name: 'Screen Replacement', url: 'https://ifixappleservicecenter.in/iphone-screen-replacement' },
]);

const screenFaqs = [
  {
    question: 'How long does iPhone screen replacement take in Patna?',
    answer: 'Most iPhone screen replacements are completed within 2-4 hours at our Patna service center. Simple LCD replacements for older models like iPhone 6/7/8 can be done in under an hour, while newer OLED models like iPhone 12-16 series may take up to 90 minutes to ensure perfect calibration and testing.',
  },
  {
    question: 'What is the cost of iPhone screen replacement in Patna?',
    answer: 'iPhone screen replacement costs vary by model and screen type (LCD/OLED). Contact us at +91 97986 53591 or WhatsApp for an accurate quote for your specific model. All replacements include a 3-month warranty.',
  },
  {
    question: 'Do you use original Apple screens for replacement?',
    answer: 'We use premium OEM-grade OLED and LCD screens that match Apple\'s specifications for color accuracy, brightness, and touch sensitivity. All screens come with True Tone support where applicable and include a comprehensive 3-month warranty covering any defects.',
  },
  {
    question: 'Will my iPhone be waterproof after screen replacement?',
    answer: 'We apply new waterproof seals during every screen replacement to maintain your iPhone\'s water resistance rating. However, we recommend avoiding water exposure for 24 hours after repair to ensure the adhesive has fully cured.',
  },
  {
    question: 'Can you fix a screen that\'s only cracked but still works?',
    answer: 'Yes, we highly recommend replacing cracked screens even if they still work. Cracks can spread over time, affect touch sensitivity, and allow dust and moisture to enter the device. Early replacement prevents more expensive internal repairs later.',
  },
  {
    question: 'Is Face ID affected by screen replacement?',
    answer: 'No, our trained technicians carefully transfer your original Face ID components during screen replacement. Your Face ID will continue to work perfectly after the repair. We also preserve True Tone calibration on supported models.',
  },
  {
    question: 'Do you provide pickup and delivery service in Patna?',
    answer: 'Yes, we offer convenient pickup and delivery service across Patna and surrounding areas. Simply book through WhatsApp or call us, and our technician will collect your device, repair it at our service center, and deliver it back to you.',
  },
  {
    question: 'What warranty do you provide on screen replacement?',
    answer: 'All our iPhone screen replacements come with a comprehensive 3-month warranty. This covers any manufacturing defects, touch issues, or display problems. We also provide a warranty card for your records and peace of mind.',
  },
];

// Bihar districts we serve
const biharDistricts = [
  'Gaya', 'Muzaffarpur', 'Chhapra', 'Siwan', 'Rohtas', 'Bhagalpur',
  'Darbhanga', 'Purnia', 'Begusarai', 'Ara', 'Purnea', 'Samastipur',
  'Bihar Sharif', 'Katihar', 'Munger', 'Gopalganj', 'Madhepura', 'Motihari',
  'Bettiah', 'Hajipur', 'Jehanabad', 'Nawada', 'Sasaram', 'Buxar'
];

// iPhone models we repair
const iphoneModels = [
  { series: 'iPhone 6 Series', models: 'iPhone 6, 6 Plus, 6s, 6s Plus' },
  { series: 'iPhone 7 & 8 Series', models: 'iPhone 7, 7 Plus, 8, 8 Plus, SE (2nd & 3rd Gen)' },
  { series: 'iPhone X Series', models: 'iPhone X, XR, XS, XS Max' },
  { series: 'iPhone 11 Series', models: 'iPhone 11, 11 Pro, 11 Pro Max' },
  { series: 'iPhone 12 Series', models: 'iPhone 12 mini, 12, 12 Pro, 12 Pro Max' },
  { series: 'iPhone 13 Series', models: 'iPhone 13 mini, 13, 13 Pro, 13 Pro Max' },
  { series: 'iPhone 14 Series', models: 'iPhone 14, 14 Plus, 14 Pro, 14 Pro Max' },
  { series: 'iPhone 15 Series', models: 'iPhone 15, 15 Plus, 15 Pro, 15 Pro Max' },
  { series: 'iPhone 16 Series', models: 'iPhone 16, 16 Plus, 16 Pro, 16 Pro Max' },
];

const IPhoneScreenReplacement = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const benefits = [
    {
      icon: Clock,
      title: 'Same-Day Repair',
      description: 'Most screen replacements completed within 2-4 hours',
    },
    {
      icon: Shield,
      title: '3-Month Warranty',
      description: 'Comprehensive warranty on all screen replacements',
    },
    {
      icon: Award,
      title: 'Genuine Quality Parts',
      description: 'We use only authentic Apple-quality display panels',
    },
    {
      icon: Users,
      title: 'Expert Technicians',
      description: 'Apple-certified repair specialists with years of experience',
    },
    {
      icon: ThumbsUp,
      title: 'Affordable Pricing',
      description: 'Competitive rates without compromising quality',
    },
    {
      icon: Smartphone,
      title: 'All Models Supported',
      description: 'From iPhone 6 to the latest iPhone 16 Pro Max',
    },
  ];

  const screenTypes = [
    {
      name: 'LCD Screen Repair',
      models: 'iPhone 6, 6s, 7, 8, SE, XR, 11',
      description: 'High-quality LCD replacements with perfect color reproduction',
    },
    {
      name: 'OLED Screen Repair',
      models: 'iPhone X, XS, 11 Pro, 12, 13, 14, 15, 16 series',
      description: 'Premium OLED displays with deep blacks and True Tone support',
    },
  ];


  return (
    <Layout>
      <SEOHead
        title="iPhone Screen Replacement Patna | iPhone Repair Shop Near Me - iFix Apple Care"
        description="Best iPhone screen replacement service in Patna, Bihar. Same-day OLED & LCD screen repair for iPhone 6-16 Pro Max at our iPhone service center near me. iPhone authorised service center quality with 3-month warranty. Starts ₹2,499. Book now!"
        url="https://ifixappleservicecenter.in/iphone-service/screen-replacement"
      />
      <JsonLd data={screenReplacementSchema} />
      <JsonLd data={screenBreadcrumbSchema} />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <OptimizedImage
            src={iphoneImage}
            alt="iPhone screen replacement service in Patna Bihar - cracked screen repair OLED LCD fix by certified technicians"
            className="w-full h-full object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
        </div>
        
        <div className="container-apple relative z-10 py-20">
          <PageBreadcrumb 
            items={[
              { name: 'iPhone Service', path: '/iphone-service' },
              { name: 'Screen Replacement', path: '/iphone-service/screen-replacement' },
            ]} 
            className="mb-8"
          />
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-tight mb-6 opacity-0 animate-fade-in">
            iPhone Screen Replacement
            <span className="block text-primary">in Patna, Bihar</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-4 max-w-2xl leading-relaxed opacity-0 animate-fade-in animation-delay-100">
            Is your iPhone screen cracked or damaged? Looking for a reliable iPhone repair shop in Patna or an iPhone service center near me? 
            <strong className="text-foreground"> iFix Apple Care</strong> is your trusted Apple service center in Patna for professional iPhone screen replacement with same-day service.
          </p>
          
          <p className="text-base text-muted-foreground mb-8 max-w-2xl leading-relaxed opacity-0 animate-fade-in animation-delay-150">
            We understand how important your iPhone is. That's why we offer the best iPhone repair service at our i phone service center near me in Bihar with authorised Apple service center quality parts.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in animation-delay-200">
            <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
              <Calendar className="w-5 h-5" />
              Book Screen Replacement
            </Button>
            <Button variant="whatsapp" size="lg" asChild>
              <a
                href="https://wa.me/919798653591?text=Hi,%20I%20need%20iPhone%20screen%20replacement%20in%20Patna"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp Us
              </a>
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            Why Choose Our Apple iPhone Service Center for iPhone Screen Repair?
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
            At iFix Apple Care - your nearest Apple service centre in Patna - we offer the best iPhone repair experience with iPhone authorised service center quality. Whether you're searching for "iPhone service center near me" or "Apple repair shop near me", we've got you covered.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.title}
                className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all opacity-0 animate-fade-in text-center"
                style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <benefit.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* iPhone Models Section */}
      <section className="section-padding bg-secondary">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            iPhone Models We Repair in Patna
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
            Our iPhone service center in Patna handles all iPhone models from iPhone 6 to the latest iPhone 16 Pro Max. Whatever model you have, we've got you covered with expert screen replacement service.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {iphoneModels.map((item, index) => (
              <div
                key={item.series}
                className="p-5 bg-background rounded-2xl border border-border hover:border-primary/30 transition-all opacity-0 animate-fade-in"
                style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
              >
                <h3 className="font-semibold text-foreground mb-2">{item.series}</h3>
                <p className="text-sm text-muted-foreground">{item.models}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screen Types */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            Screen Types We Replace
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
            We stock both LCD and OLED screens for all iPhone models, ensuring perfect compatibility and display quality that matches Apple's original specifications.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {screenTypes.map((type) => (
              <div key={type.name} className="p-8 bg-card rounded-2xl border border-border hover:border-primary/30 transition-all">
                <h3 className="text-xl font-semibold text-foreground mb-2">{type.name}</h3>
                <p className="text-primary font-medium mb-3">{type.models}</p>
                <p className="text-muted-foreground">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Process Section */}
      <section className="section-padding bg-secondary">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            Our iPhone Screen Repair Process
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
            When you bring your iPhone to our Apple service center in Patna, here's what you can expect. Our professional repair process ensures quality and safety of your device.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {[
              { step: '1', title: 'Free Diagnosis', description: 'We examine your device and identify the exact issue with your iPhone screen' },
              { step: '2', title: 'Transparent Quote', description: 'You get a clear price estimate before any work begins - no hidden charges' },
              { step: '3', title: 'Expert Repair', description: 'Our certified technicians replace your screen with precision and care' },
              { step: '4', title: 'Quality Testing', description: 'Every repair undergoes thorough testing of display, touch, and Face ID before handover' },
              { step: '5', title: 'Warranty Card', description: 'You receive warranty documentation for peace of mind and future reference' },
            ].map((item, index) => (
              <div key={item.step} className="text-center opacity-0 animate-fade-in" style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}>
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Issues */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            Screen Issues We Fix
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            From minor cracks to complete display failure, we handle all screen problems.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              'Cracked or shattered screen',
              'Unresponsive touch screen',
              'Black screen / no display',
              'Lines or pixels on screen',
              'Ghost touch issues',
              'Screen flickering',
              'LCD bleeding',
              'Touch not working partially',
              'Screen discoloration',
            ].map((issue, index) => (
              <div
                key={issue}
                className="flex items-center gap-3 p-4 bg-card rounded-2xl border border-border opacity-0 animate-fade-in"
                style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="font-medium text-foreground">{issue}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Serving All Bihar Section */}
      <section className="section-padding">
        <div className="container-apple">
          <div className="flex items-center justify-center gap-3 mb-4">
            <MapPin className="w-8 h-8 text-primary" />
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground text-center">
              Serving All of Bihar
            </h2>
          </div>
          <p className="text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
            While our main service center is located in Patna, we proudly serve customers from across Bihar. Whether you're in Gaya, Muzaffarpur, or any other district, you can trust iFix Apple Care for reliable iPhone screen repair.
          </p>
          
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {biharDistricts.map((district, index) => (
              <span
                key={district}
                className="px-4 py-2 bg-card rounded-full text-sm font-medium text-foreground border border-border hover:border-primary/30 transition-all opacity-0 animate-fade-in"
                style={{ animationDelay: `${index * 30}ms`, animationFillMode: 'forwards' }}
              >
                {district}
              </span>
            ))}
            <span className="px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary border border-primary/20">
              + All Districts of Bihar
            </span>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        title="iPhone Screen Replacement FAQs"
        subtitle="Common questions about iPhone screen replacement service in Patna, Bihar"
        faqs={screenFaqs}
      />

      {/* Related Services */}
      <section className="section-padding bg-secondary">
        <div className="container-apple text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Other iPhone Repair Services in Patna
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Apart from screen replacement, we offer comprehensive iPhone repair services at our Patna service center.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/iphone-service/battery-replacement"
              className="px-6 py-3 bg-background rounded-full text-foreground font-medium border border-border hover:border-primary/30 transition-all"
            >
              Battery Replacement
            </Link>
            <Link
              to="/iphone-service"
              className="px-6 py-3 bg-background rounded-full text-foreground font-medium border border-border hover:border-primary/30 transition-all"
            >
              All iPhone Services
            </Link>
            <Link
              to="/blog/iphone-water-damage-recovery-patna"
              className="px-6 py-3 bg-background rounded-full text-foreground font-medium border border-border hover:border-primary/30 transition-all"
            >
              Water Damage Recovery
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-foreground">
        <div className="container-apple text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-background mb-4">
            Get Your iPhone Screen Fixed Today in Patna
          </h2>
          <p className="text-background/70 mb-8 max-w-2xl mx-auto">
            Don't let a cracked screen ruin your iPhone experience. Visit iFix Apple Care in Patna for professional screen replacement with same-day service and 3-month warranty. We're Bihar's most trusted Apple service center.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
              <Calendar className="w-5 h-5" />
              Book Screen Replacement
            </Button>
            <Button variant="whatsapp" size="lg" asChild>
              <a
                href="https://wa.me/919798653591?text=Hi,%20I%20need%20iPhone%20screen%20replacement%20in%20Patna"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp Us
              </a>
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </Layout>
  );
};

export default IPhoneScreenReplacement;
